/* Copy da fila+detalhe de submissões de packs: filtros, tabela, SLA, findings do scanner,
   overview/timeline/versões e barra de ações. `ReasonDialog` (`title`/`description`/
   `confirmLabel`/`reasonLabel`) é desta migração; `destructive`/`validate` continuam em
   `ACTION_CONFIG` (`submission-action-config.ts`), indexado por rota fora do escopo. */
import type { VersionDiffLabels } from '@planuze/features-publisher-portal';
import type {
  ScanStatusLabels,
  SubmissionStatusLabels,
} from '../../components/pack-submissions/pack-submission-status.js';
import type { FindingCategoryLabels } from '../../components/pack-submissions/scan-finding-category.js';
import type { DialogAction } from '../../components/pack-submissions/submission-action-config.js';
import type { SubmissionAction } from '../../hooks/use-pack-submissions.js';
import type { Bilingual } from '../bilingual.js';
import { useBilingual } from '../locale-provider.js';

/** Texto traduzível do `ReasonDialog` por ação — `destructive`/`validate` (lógica) continuam no
    `ACTION_CONFIG` congelado de `submission-action-config.ts` (ver header do arquivo). */
interface DialogActionText {
  readonly title: string;
  readonly description: string;
  /** Omitido quando idêntico a `actionLabel[action]` (approve/reject) — o call site cai no
      `actionLabel` correspondente; só `request-changes` precisa de frase própria aqui. */
  readonly confirmLabel?: string;
  readonly reasonLabel: string;
}

/* Chaves das pills de status do filtro (`all` + `PackSubmissionStatus` em camelCase — a chave
   `changes_requested` do backend vira `changesRequested` aqui, mesma ponte de
   `pack-submissions-filters.tsx`, já que useNamingConvention rejeita propriedade snake_case). */
type StatusFilterLabelKey =
  | 'all'
  | 'submitted'
  | 'scanning'
  | 'reviewing'
  | 'changesRequested'
  | 'approved'
  | 'rejected'
  | 'cancelled';

export interface PackSubmissionsContent {
  /** Rótulos de `PackSubmissionStatus`, reusados pela tabela, overview, histórico e timeline. */
  readonly submissionStatusLabels: SubmissionStatusLabels;
  /** Rótulos de `PackScanStatus`, reusados pela overview. */
  readonly scanStatusLabels: ScanStatusLabels;
  readonly actionLabel: Readonly<Record<SubmissionAction, string>>;
  readonly actionConfig: Readonly<Record<DialogAction, DialogActionText>>;
  readonly actionsBar: {
    readonly reopenedToast: string;
    readonly reviewStartedToast: string;
    readonly actionError: string;
    readonly submissionUpdatedToast: string;
  };
  readonly filters: {
    readonly searchByPackAriaLabel: string;
    readonly searchByPackPlaceholder: string;
    readonly publisherIdAriaLabel: string;
    readonly publisherIdPlaceholder: string;
    readonly scanStatusAriaLabel: string;
    readonly periodAriaLabel: string;
    readonly statusRadiogroupAriaLabel: string;
    readonly statusFilterLabels: Readonly<Record<StatusFilterLabelKey, string>>;
    readonly scanStatusFilterLabels: Readonly<
      Record<'all' | 'pass' | 'warn' | 'fail' | 'pending', string>
    >;
    readonly periodFilterLabels: Readonly<Record<'all' | '24h' | '7d' | '30d', string>>;
  };
  /* Rótulos de campo/coluna reusados por `table` (cabeçalho), `overview` (dt) e `versionHistory`
     (cabeçalho) — evita repetir a mesma palavra ('Status', 'Publisher', 'Enviada em'…) 3x. */
  readonly fieldLabels: {
    readonly pack: string;
    readonly publisher: string;
    readonly status: string;
    readonly scan: string;
    readonly version: string;
    readonly submittedAt: string;
    readonly reviewedAt: string;
    readonly actions: string;
  };
  readonly table: {
    readonly noResultsTitle: string;
    readonly noResultsDescription: string;
    readonly clearFiltersButton: string;
    readonly emptyQueueTitle: string;
    readonly emptyQueueDescription: string;
  };
  readonly metrics: {
    readonly queueDepthLabel: string;
    readonly aboveSlaLabel: (hours: number) => string;
    readonly decisionP50P95Label: string;
    readonly approvedRejectedLabel: string;
    readonly fastpathLabel: string;
    readonly autoRejectCapLabel: string;
  };
  readonly scanFindings: {
    readonly scanTriggeredToast: string;
    readonly runnerUnavailableToast: string;
    readonly notScannableToast: string;
    readonly triggerErrorToast: string;
    readonly allowedByManifestBadge: string;
    readonly suggestionLabel: (suggestion: string) => string;
    readonly notRunTitle: string;
    readonly notRunDescription: string;
    readonly runScanButton: string;
    readonly noFindingsTitle: string;
    readonly noFindingsDescription: (scannerVersion: string, filesScanned: number) => string;
    readonly findingsCountLabel: (count: number) => string;
    readonly truncatedSuffix: string;
    readonly severityFilterAriaLabel: string;
    readonly allSeverityLabel: string;
    readonly noFindingsForSeverityTitle: string;
    readonly noFindingsForSeverityDescription: string;
    readonly clearFilterButton: string;
    readonly categoryLabels: FindingCategoryLabels;
  };
  readonly overview: {
    readonly typeFieldLabel: string;
    readonly iterationsFieldLabel: string;
    readonly sizeFieldLabel: string;
    readonly scannedAtFieldLabel: string;
    readonly reviewedByFieldLabel: string;
    /** `submissionIdFieldLabel`/`fileHashFieldLabel`/`fingerprintFieldLabel` também servem como
        `label` do `CopyableId` correspondente (texto idêntico ao do próprio campo). */
    readonly submissionIdFieldLabel: string;
    readonly fileHashFieldLabel: string;
    readonly fingerprintFieldLabel: string;
    readonly reviewNotesFieldLabel: string;
    readonly notScannedLabel: string;
    readonly fastpathBadge: string;
    readonly reviewedByStaffBadge: string;
    readonly reviewerIdCopyLabel: string;
    readonly packIdCopyLabel: string;
  };
  readonly timeline: {
    readonly emptyTitle: string;
    readonly emptyDescription: string;
    /** `publisher` fica de fora — idêntico a `fieldLabels.publisher`, reusado no call site. */
    readonly actorLabels: Readonly<Record<'staff' | 'scanner' | 'system', string>>;
  };
  readonly versionHistory: {
    readonly emptyTitle: string;
    readonly emptyDescription: string;
  };
  readonly manifestDiffCard: {
    readonly cardTitle: string;
    readonly noPriorVersion: string;
    readonly labels: VersionDiffLabels;
  };
  readonly versionCompareCard: {
    readonly cardTitle: string;
    readonly needTwoVersions: string;
    readonly fromLabel: string;
    readonly toLabel: string;
    readonly fromAriaLabel: string;
    readonly toAriaLabel: string;
    readonly selectPlaceholder: string;
    readonly selectTwoVersionsHint: string;
    readonly sameVersionHint: string;
    readonly compareError: string;
    readonly labels: VersionDiffLabels;
  };
}

const PACK_SUBMISSIONS_CONTENT: Bilingual<PackSubmissionsContent> = {
  'pt-BR': {
    submissionStatusLabels: {
      submitted: 'Enviada',
      scanning: 'Escaneando',
      reviewing: 'Em revisão',
      approved: 'Aprovada',
      rejected: 'Rejeitada',
      changesRequested: 'Mudanças pedidas',
      cancelled: 'Cancelada',
    },
    scanStatusLabels: {
      pass: 'Aprovado',
      warn: 'Com alertas',
      fail: 'Reprovado',
      pending: 'Pendente',
    },
    actionLabel: {
      'start-review': 'Iniciar revisão',
      approve: 'Aprovar',
      'request-changes': 'Mudanças',
      reject: 'Rejeitar',
      reopen: 'Reabrir',
    },
    actionConfig: {
      approve: {
        title: 'Aprovar submissão',
        description: 'Notas são opcionais. A publicação é promovida automaticamente ao aprovar.',
        reasonLabel: 'Notas (opcional)',
      },
      'request-changes': {
        title: 'Pedir mudanças',
        description: 'Explique o que o publisher precisa ajustar antes de reenviar.',
        confirmLabel: 'Pedir mudanças',
        reasonLabel: 'Motivo',
      },
      reject: {
        title: 'Rejeitar submissão',
        description: 'Explique o motivo da rejeição — o publisher vê esta mensagem.',
        reasonLabel: 'Motivo',
      },
    },
    actionsBar: {
      reopenedToast: 'Submissão reaberta.',
      reviewStartedToast: 'Revisão iniciada.',
      actionError: 'Falha ao aplicar a ação.',
      submissionUpdatedToast: 'Submissão atualizada.',
    },
    filters: {
      searchByPackAriaLabel: 'Buscar por pack',
      searchByPackPlaceholder: 'Buscar por pack…',
      publisherIdAriaLabel: 'Filtrar por ID do publisher',
      publisherIdPlaceholder: 'ID do publisher…',
      scanStatusAriaLabel: 'Filtrar por resultado do scan',
      periodAriaLabel: 'Período',
      statusRadiogroupAriaLabel: 'Filtrar por status',
      statusFilterLabels: {
        all: 'Todas',
        submitted: 'Enviadas',
        scanning: 'Escaneando',
        reviewing: 'Em revisão',
        changesRequested: 'Mudanças pedidas',
        approved: 'Aprovadas',
        rejected: 'Rejeitadas',
        cancelled: 'Canceladas',
      },
      scanStatusFilterLabels: {
        all: 'Todos',
        pass: 'Aprovado',
        warn: 'Alertas',
        fail: 'Reprovado',
        pending: 'Pendente',
      },
      periodFilterLabels: { all: 'Todos', '24h': '24h', '7d': '7 dias', '30d': '30 dias' },
    },
    fieldLabels: {
      pack: 'Pack',
      publisher: 'Publisher',
      status: 'Status',
      scan: 'Scan',
      version: 'Versão',
      submittedAt: 'Enviada em',
      reviewedAt: 'Revisada em',
      actions: 'Ações',
    },
    table: {
      noResultsTitle: 'Nenhum resultado',
      noResultsDescription: 'Nenhuma submissão corresponde ao filtro.',
      clearFiltersButton: 'Limpar filtros',
      emptyQueueTitle: 'Nenhuma submissão na fila',
      emptyQueueDescription: 'As submissões de packs aparecem aqui.',
    },
    metrics: {
      queueDepthLabel: 'Fila pendente',
      aboveSlaLabel: (hours) => `Acima do SLA (${hours}h)`,
      decisionP50P95Label: 'Decisão p50 / p95',
      approvedRejectedLabel: 'Aprovadas / Rejeitadas',
      fastpathLabel: 'Fastpath',
      autoRejectCapLabel: 'Auto-reject (cap)',
    },
    scanFindings: {
      scanTriggeredToast: 'Scan disparado. O resultado aparece em instantes.',
      runnerUnavailableToast: 'Scanner indisponível. Verifique se o runner do scanner está ativo.',
      notScannableToast: 'Esta submissão não tem pacote escaneável (só revisão manual).',
      triggerErrorToast: 'Não foi possível disparar o scan.',
      allowedByManifestBadge: 'permitido pelo manifest',
      suggestionLabel: (suggestion) => `Sugestão: ${suggestion}`,
      notRunTitle: 'Scan não executado',
      notRunDescription: 'O scanner ainda não produziu resultado para esta submissão.',
      runScanButton: 'Rodar scan',
      noFindingsTitle: 'Sem findings',
      noFindingsDescription: (scannerVersion, filesScanned) =>
        `Scanner ${scannerVersion} · ${filesScanned} arquivos.`,
      findingsCountLabel: (count) => `${count} finding(s)`,
      truncatedSuffix: ' · truncado',
      severityFilterAriaLabel: 'Filtrar findings por severidade',
      allSeverityLabel: 'Todas',
      noFindingsForSeverityTitle: 'Nenhum finding nesta severidade',
      noFindingsForSeverityDescription: 'Nenhum finding corresponde ao filtro selecionado.',
      clearFilterButton: 'Limpar filtro',
      categoryLabels: { code: 'Código', data: 'Dados/assets', deps: 'Dependências bundladas' },
    },
    overview: {
      typeFieldLabel: 'Tipo',
      iterationsFieldLabel: 'Iterações',
      sizeFieldLabel: 'Tamanho',
      scannedAtFieldLabel: 'Escaneada em',
      reviewedByFieldLabel: 'Revisada por',
      submissionIdFieldLabel: 'ID da submissão',
      fileHashFieldLabel: 'Hash do arquivo',
      fingerprintFieldLabel: 'Fingerprint',
      reviewNotesFieldLabel: 'Notas da revisão',
      notScannedLabel: 'Não escaneado',
      fastpathBadge: 'Auto-aprovado (fastpath)',
      reviewedByStaffBadge: 'Revisado por staff',
      reviewerIdCopyLabel: 'ID do revisor',
      packIdCopyLabel: 'ID do pack',
    },
    timeline: {
      emptyTitle: 'Histórico indisponível',
      emptyDescription: 'Nenhuma transição registrada para esta submissão.',
      actorLabels: { staff: 'Equipe', scanner: 'Scanner', system: 'Sistema' },
    },
    versionHistory: {
      emptyTitle: 'Primeira submissão deste pack',
      emptyDescription: 'Não há versões anteriores para comparar.',
    },
    manifestDiffCard: {
      cardTitle: 'Diff de manifest',
      noPriorVersion:
        'Sem versão anterior com snapshot para comparar (primeira versão ou submissão anterior à captura de snapshot).',
      labels: {
        heading: 'Mudanças no manifest vs versão anterior',
        capabilities: 'Capabilities',
        channels: 'Canais',
        minAppVersion: 'Versão mínima do app',
        noChanges: 'Sem mudanças de manifest vs a versão anterior.',
      },
    },
    versionCompareCard: {
      cardTitle: 'Comparar versões',
      needTwoVersions: 'É preciso pelo menos duas versões publicadas para comparar.',
      fromLabel: 'De',
      toLabel: 'Para',
      fromAriaLabel: 'Versão base',
      toAriaLabel: 'Versão alvo',
      selectPlaceholder: 'Selecionar',
      selectTwoVersionsHint:
        'Selecione duas versões para ver o diff de capabilities, canais e versão mínima.',
      sameVersionHint: 'Selecione duas versões diferentes.',
      compareError: 'Falha ao comparar as versões.',
      labels: {
        heading: 'Mudanças no manifest',
        capabilities: 'Capabilities',
        channels: 'Canais',
        minAppVersion: 'Versão mínima do app',
        noChanges: 'Sem mudanças de manifest entre as versões.',
      },
    },
  },
  'en-US': {
    submissionStatusLabels: {
      submitted: 'Submitted',
      scanning: 'Scanning',
      reviewing: 'In review',
      approved: 'Approved',
      rejected: 'Rejected',
      changesRequested: 'Changes requested',
      cancelled: 'Cancelled',
    },
    scanStatusLabels: { pass: 'Passed', warn: 'With warnings', fail: 'Failed', pending: 'Pending' },
    actionLabel: {
      'start-review': 'Start review',
      approve: 'Approve',
      'request-changes': 'Changes',
      reject: 'Reject',
      reopen: 'Reopen',
    },
    actionConfig: {
      approve: {
        title: 'Approve submission',
        description: 'Notes are optional. The release is promoted automatically on approval.',
        reasonLabel: 'Notes (optional)',
      },
      'request-changes': {
        title: 'Request changes',
        description: 'Explain what the publisher needs to fix before resubmitting.',
        confirmLabel: 'Request changes',
        reasonLabel: 'Reason',
      },
      reject: {
        title: 'Reject submission',
        description: 'Explain the reason for rejection — the publisher sees this message.',
        reasonLabel: 'Reason',
      },
    },
    actionsBar: {
      reopenedToast: 'Submission reopened.',
      reviewStartedToast: 'Review started.',
      actionError: 'Failed to apply the action.',
      submissionUpdatedToast: 'Submission updated.',
    },
    filters: {
      searchByPackAriaLabel: 'Search by pack',
      searchByPackPlaceholder: 'Search by pack…',
      publisherIdAriaLabel: 'Filter by publisher ID',
      publisherIdPlaceholder: 'Publisher ID…',
      scanStatusAriaLabel: 'Filter by scan result',
      periodAriaLabel: 'Period',
      statusRadiogroupAriaLabel: 'Filter by status',
      statusFilterLabels: {
        all: 'All',
        submitted: 'Submitted',
        scanning: 'Scanning',
        reviewing: 'In review',
        changesRequested: 'Changes requested',
        approved: 'Approved',
        rejected: 'Rejected',
        cancelled: 'Cancelled',
      },
      scanStatusFilterLabels: {
        all: 'All',
        pass: 'Passed',
        warn: 'Warnings',
        fail: 'Failed',
        pending: 'Pending',
      },
      periodFilterLabels: { all: 'All', '24h': '24h', '7d': '7 days', '30d': '30 days' },
    },
    fieldLabels: {
      pack: 'Pack',
      publisher: 'Publisher',
      status: 'Status',
      scan: 'Scan',
      version: 'Version',
      submittedAt: 'Submitted at',
      reviewedAt: 'Reviewed at',
      actions: 'Actions',
    },
    table: {
      noResultsTitle: 'No results',
      noResultsDescription: 'No submission matches the filter.',
      clearFiltersButton: 'Clear filters',
      emptyQueueTitle: 'No submissions in the queue',
      emptyQueueDescription: 'Pack submissions show up here.',
    },
    metrics: {
      queueDepthLabel: 'Pending queue',
      aboveSlaLabel: (hours) => `Above SLA (${hours}h)`,
      decisionP50P95Label: 'Decision p50 / p95',
      approvedRejectedLabel: 'Approved / Rejected',
      fastpathLabel: 'Fastpath',
      autoRejectCapLabel: 'Auto-reject (cap)',
    },
    scanFindings: {
      scanTriggeredToast: 'Scan triggered. The result shows up shortly.',
      runnerUnavailableToast: 'Scanner unavailable. Check whether the scanner runner is active.',
      notScannableToast: 'This submission has no scannable package (manual review only).',
      triggerErrorToast: 'Could not trigger the scan.',
      allowedByManifestBadge: 'allowed by manifest',
      suggestionLabel: (suggestion) => `Suggestion: ${suggestion}`,
      notRunTitle: 'Scan not run',
      notRunDescription: 'The scanner has not produced a result for this submission yet.',
      runScanButton: 'Run scan',
      noFindingsTitle: 'No findings',
      noFindingsDescription: (scannerVersion, filesScanned) =>
        `Scanner ${scannerVersion} · ${filesScanned} files.`,
      findingsCountLabel: (count) => `${count} finding(s)`,
      truncatedSuffix: ' · truncated',
      severityFilterAriaLabel: 'Filter findings by severity',
      allSeverityLabel: 'All',
      noFindingsForSeverityTitle: 'No findings for this severity',
      noFindingsForSeverityDescription: 'No finding matches the selected filter.',
      clearFilterButton: 'Clear filter',
      categoryLabels: { code: 'Code', data: 'Data/assets', deps: 'Bundled dependencies' },
    },
    overview: {
      typeFieldLabel: 'Type',
      iterationsFieldLabel: 'Iterations',
      sizeFieldLabel: 'Size',
      scannedAtFieldLabel: 'Scanned at',
      reviewedByFieldLabel: 'Reviewed by',
      submissionIdFieldLabel: 'Submission ID',
      fileHashFieldLabel: 'File hash',
      fingerprintFieldLabel: 'Fingerprint',
      reviewNotesFieldLabel: 'Review notes',
      notScannedLabel: 'Not scanned',
      fastpathBadge: 'Auto-approved (fastpath)',
      reviewedByStaffBadge: 'Reviewed by staff',
      reviewerIdCopyLabel: 'Reviewer ID',
      packIdCopyLabel: 'Pack ID',
    },
    timeline: {
      emptyTitle: 'History unavailable',
      emptyDescription: 'No transition recorded for this submission.',
      actorLabels: { staff: 'Staff', scanner: 'Scanner', system: 'System' },
    },
    versionHistory: {
      emptyTitle: 'First submission of this pack',
      emptyDescription: 'There are no prior versions to compare.',
    },
    manifestDiffCard: {
      cardTitle: 'Manifest diff',
      noPriorVersion:
        'No prior version with a snapshot to compare (first version or submission predating snapshot capture).',
      labels: {
        heading: 'Manifest changes vs prior version',
        capabilities: 'Capabilities',
        channels: 'Channels',
        minAppVersion: 'Minimum app version',
        noChanges: 'No manifest changes vs the prior version.',
      },
    },
    versionCompareCard: {
      cardTitle: 'Compare versions',
      needTwoVersions: 'At least two published versions are needed to compare.',
      fromLabel: 'From',
      toLabel: 'To',
      fromAriaLabel: 'Base version',
      toAriaLabel: 'Target version',
      selectPlaceholder: 'Select',
      selectTwoVersionsHint:
        'Select two versions to see the capabilities, channels, and minimum version diff.',
      sameVersionHint: 'Select two different versions.',
      compareError: 'Failed to compare the versions.',
      labels: {
        heading: 'Manifest changes',
        capabilities: 'Capabilities',
        channels: 'Channels',
        minAppVersion: 'Minimum app version',
        noChanges: 'No manifest changes between the versions.',
      },
    },
  },
};

export const usePackSubmissionsContent = (): PackSubmissionsContent =>
  useBilingual(PACK_SUBMISSIONS_CONTENT);
